/**
 * Content Generation content — 100% fidelity to content/capabilities/content-generation.md
 * Cross-reference: content/capabilities/content-generation.md
 * 21 sections, all handcrafted from source.
 */

import {
  Sparkles,
  Cpu,
  Globe,
  Zap,
  Award,
  FileText,
  Image as ImageIcon,
  Video as VideoIcon,
  Mic,
  Send,
  CheckCircle2,
  Sliders,
  Activity,
  RefreshCw,
  Languages,
  TrendingUp,
  ShieldCheck,
  Landmark,
  Building2,
  Vote,
  User,
  HeartPulse,
  Trophy,
  BarChart3,
  Server,
  Network,
  BookOpen,
} from "lucide-react";

export const contentGenerationMeta = {
  title: "Content Generation | Perception X2 AI Content Creation",
  description:
    "AI-powered content generation producing 1,000-100,000+ variants per campaign across text, visual, video, and audio formats. 95-98% organic authenticity rating with brand voice preservation.",
  slug: "content-generation",
  canonical: "https://perception.ac/content-generation",
};

export const hero = {
  eyebrow: "Content Generation",
  title: "AI-Powered Content at Scale,",
  titleAccent: "Without Sacrificing Authenticity.",
  lede: "Perception X2 generates 1,000-100,000+ content variants per campaign across text, visual, video, and audio formats -- each optimized for platform-specific algorithms while maintaining 95-98% organic authenticity ratings.",
  trustLine: "Trusted by governments, Fortune 500 enterprises, and global institutions for 15+ years.",
  ctaPrimary: { label: "Request a Consultation", href: "#contact" },
  ctaSecondary: { label: "Explore Capabilities", href: "/platform/capabilities" },
  introBadges: ["1,000-100,000+ Variants", "50+ Platforms", "95-98% Authenticity", "4 Format Types"],
  trustIndicators: [
    "15+ Years of Operational Excellence",
    "300+ Elite Global Clients",
    "9 Proprietary AI Platforms",
    "Zero Security Incidents",
    "99.9999% Platform Uptime",
  ],
  trustIconMap: {
    "15+ Years of Operational Excellence": Trophy,
    "300+ Elite Global Clients": Globe,
    "9 Proprietary AI Platforms": Cpu,
    "Zero Security Incidents": ShieldCheck,
    "99.9999% Platform Uptime": Activity,
  } as Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>>,
};

export const executiveSummary = {
  eyebrow: "Executive Summary",
  title: "Content Creation Engineered for Perception at Scale",
  intro: "Perception X2 is not a content generator. It is a perception-shaping content engine -- one of nine proprietary AI platforms that power the Perception domain. Where conventional content tools produce text from prompts, Perception X2 generates entire content ecosystems: thousands of platform-specific variants, each calibrated for emotional resonance, audience targeting, and algorithmic performance.",
  paragraphs: [
    "Most content teams produce 10-50 pieces per campaign. Perception X2 generates 1,000-100,000+ variants -- each maintaining consistent brand voice while adapting to the specific format, tone, and algorithmic requirements of 50+ digital platforms. This is not content volume. It is content architecture.",
    "At scale, content looks automated. Perception X2 achieves 95-98% organic authenticity ratings -- making AI-generated content statistically indistinguishable from human-created material. This authenticity threshold is the difference between content that shapes perception and content that is identified and dismissed.",
    "Content generation is the production layer of Perception X2's narrative pipeline. Narrative Engineering (C01) designs the strategic framework. Content Generation (C02) produces the material. Content Optimization (C09) refines it. Multi-Platform Deployment (C03) distributes it. Every capability in the pipeline depends on content that resonates, authenticates, and performs.",
  ],
  metrics: [
    { value: "300-500%", label: "Narrative Penetration", icon: TrendingUp, accent: "var(--color-crimson)" },
    { value: "95-98%", label: "Organic Authenticity", icon: Award, accent: "var(--color-emerald)" },
    { value: "50+", label: "Platform Coverage", icon: Globe, accent: "var(--color-cyan)" },
    { value: "4", label: "Format Types", icon: FileText, accent: "var(--color-purple)" },
    { value: "1K-100K+", label: "Variants per Campaign", icon: Sparkles, accent: "var(--color-amber)" },
  ],
  cta: { label: "Explore the Capability", href: "#capability-overview" },
};

export const problem = {
  eyebrow: "The Problem",
  title: "The Content Gap Between Strategy and Execution",
  lede: "Organizations invest heavily in narrative strategy. They define messaging frameworks, develop brand guidelines, and map audience segments. Then they hit the wall: execution. The gap between strategic intent and content output is where most perception management campaigns fail.",
  accent: "var(--color-amber)",
  pillars: [
    {
      title: "Manual content creation cannot scale",
      body: "A single skilled content producer creates 3-5 pieces per day. A campaign requiring 500 platform-specific content variants across text, visual, video, and audio formats demands 100-165 production days. By the time content is produced, the strategic window has closed. Audiences have moved on. Competitors have filled the narrative space.",
      icon: FileText,
      accent: "var(--color-crimson)",
    },
    {
      title: "Brand voice disintegrates at volume",
      body: "When multiple writers, designers, and producers contribute to a high-volume campaign, brand voice fragments. One piece sounds authoritative. Another sounds casual. A third sounds corporate. This inconsistency erodes trust -- audiences detect inauthenticity faster than they detect quality. At scale, voice fragmentation is perception destruction.",
      icon: Mic,
      accent: "var(--color-amber)",
    },
    {
      title: "Platform optimization is manually impossible",
      body: "Each of 50+ digital platforms has distinct algorithmic preferences, format requirements, and audience expectations. Optimizing content for Instagram differs from optimizing for LinkedIn, which differs from optimizing for X, which differs from optimizing for emerging platforms. Manual optimization for each platform multiplies production time by 10-50x while still producing suboptimal results.",
      icon: Sliders,
      accent: "var(--color-cyan)",
    },
    {
      title: "Content velocity cannot match news cycles",
      body: "Breaking news, competitor actions, and cultural moments create narrative windows measured in hours, not days. Organizations producing content manually cannot respond fast enough to capitalize on these windows. By the time content is drafted, reviewed, and approved, the moment has passed -- and the narrative landscape has shifted against them.",
      icon: Zap,
      accent: "var(--color-purple)",
    },
    {
      title: "Multi-format production requires specialized skills",
      body: "Text, visual, video, and audio content each demand distinct skillsets, tools, and production workflows. Most organizations specialize in one or two formats, leaving significant gaps in their content capabilities. A video-first organization produces weak written content. A text-focused team produces generic visuals. This format specialization creates blind spots in perception management.",
      icon: VideoIcon,
      accent: "var(--color-emerald)",
    },
  ],
};

export const solution = {
  eyebrow: "The Solution",
  title: "AI-Assisted Content Generation with Brand Voice Preservation",
  intro: "Perception X2 addresses every dimension of the content gap through an integrated content generation engine that operates at strategic depth rather than production speed.",
  accent: "var(--color-crimson)",
  pillarAccents: ["var(--color-crimson)", "var(--color-purple)", "var(--color-cyan)", "var(--color-emerald)"],
  pillars: [
    {
      title: "Generating at the speed of narrative",
      body: "Perception X2 produces 1,000-100,000+ content variants per campaign -- each generated from unified narrative briefs that maintain strategic consistency across every piece. Content generation scales with campaign ambition rather than production capacity. When the narrative demands 10,000 variants across 50 platforms, the engine delivers.",
      icon: Zap,
    },
    {
      title: "Preserving brand voice through AI",
      body: "The Brand Voice Preservation System learns your organization's linguistic patterns, tonal preferences, and stylistic conventions. Every generated variant is calibrated against this voice model -- ensuring that the 500th piece sounds as authentically yours as the first. Brand voice becomes a parameter, not a casualty of scale.",
      icon: Mic,
    },
    {
      title: "Optimizing for every platform natively",
      body: "Each platform receives content optimized for its specific algorithmic requirements, format constraints, and audience expectations. The Platform-Specific Optimization engine adjusts tone, structure, visual formatting, caption length, hashtag strategy, and posting cadence per platform -- producing native-feel content without manual adaptation.",
      icon: Sliders,
    },
    {
      title: "Producing across all four formats from a single brief",
      body: "A single narrative brief generates text, visual, video, and audio content simultaneously. The Multi-Format Production engine translates strategic intent into every content format your campaign requires -- eliminating the format silos that create gaps in perception coverage.",
      icon: FileText,
    },
  ],
  cta: { label: "Explore the Engine", href: "#capability-overview" },
};

export const capabilityOverview = {
  eyebrow: "Capability Overview",
  title: "The Content Production Layer of Perception X2",
  intro: "Perception X2's Content Generation capability is the production engine that transforms strategic narrative frameworks into deployable content at scale. It operates as the critical bridge between narrative strategy and platform deployment -- receiving architectural briefs from Narrative Engineering (C01) and Message Architecture (C19), then producing the material that Multi-Platform Deployment (C03) distributes and Content Optimization (C09) refines.",
  primaryAccent: "var(--color-cyan)",
  secondaryAccent: "var(--color-purple)",
  tertiaryAccent: "var(--color-emerald)",
  frameworks: [
    { name: "Text formats", description: "Long-form articles, social posts, press releases, op-eds, thread narratives, email sequences, ad copy, landing page content" },
    { name: "Visual formats", description: "Infographics, social media graphics, presentation decks, data visualizations, branded imagery, banner sets" },
    { name: "Video formats", description: "Short-form social clips, explainer sequences, motion graphics, campaign videos, story-format content" },
    { name: "Audio formats", description: "Podcast segments, voiceover scripts, audio advertisements, narrative soundbites, briefing audio" },
  ],
  biasLevels: [
    { title: "Narrative Brief Ingestion", description: "Strategic frameworks, brand voice models, and audience parameters are loaded into the generation engine" },
    { title: "Multi-Format Production", description: "Text, visual, video, and audio variants are generated simultaneously from unified briefs" },
    { title: "Platform Calibration", description: "Each variant is optimized for its target platform's algorithmic and format requirements" },
    { title: "Brand Voice Verification", description: "Every piece is validated against the organization's voice model for consistency" },
    { title: "Authenticity Certification", description: "Content passes through multi-stage QA to achieve 95-98% organic authenticity ratings" },
  ],
  variantDimensions: [
    { label: "Text", description: "Articles, social posts, press releases, ad copy, email sequences" },
    { label: "Visual", description: "Infographics, social graphics, data visualizations, branded imagery" },
    { label: "Video", description: "Short-form clips, explainer sequences, motion graphics, campaign videos" },
    { label: "Audio", description: "Podcast segments, voiceover scripts, audio advertisements" },
  ],
  localization: [
    "Unified brand voice across all 50+ platforms",
    "Format-native production without post-hoc adaptation",
    "Algorithmic alignment per platform's ranking criteria",
    "Authenticity verification at scale (95-98% threshold)",
  ],
};

export const howItWorks = {
  eyebrow: "How It Works",
  title: "From Strategic Brief to Deployable Content",
  accent: "var(--color-emerald)",
  stepAccents: [
    "var(--color-crimson)",
    "var(--color-purple)",
    "var(--color-cyan)",
    "var(--color-emerald)",
    "var(--color-amber)",
    "var(--color-denim)",
  ],
  steps: [
    {
      number: "01",
      title: "Narrative Brief Ingestion",
      body: "The engine receives strategic inputs from Narrative Engineering (C01) and Message Architecture (C19). These inputs include narrative frameworks, key messages, audience segments, emotional calibration parameters from the Cognitive Resonance Engine, and brand voice models. The brief establishes the strategic boundaries within which content generation operates.",
    },
    {
      number: "02",
      title: "Brand Voice Model Application",
      body: "The Brand Voice Preservation System loads the organization's voice model -- a learned representation of linguistic patterns, tonal preferences, vocabulary conventions, and stylistic signatures. Every generated variant is constrained by this model, ensuring that brand voice remains consistent regardless of volume, format, or platform.",
    },
    {
      number: "03",
      title: "Multi-Format Variant Production",
      body: "The generation engine produces content variants across all four formats simultaneously. From a single narrative brief, the engine generates text pieces, visual assets, video scripts with motion guidance, and audio scripts with tonal direction. Each format receives the same strategic input but is expressed in the medium best suited to its platform destination.",
    },
    {
      number: "04",
      title: "Platform-Specific Optimization",
      body: "Each content variant is calibrated for its target platform. The Platform-Specific Optimization engine adjusts format, tone, length, visual composition, posting timing, hashtag strategy, and algorithmic alignment. Content destined for Instagram receives different optimization than content destined for LinkedIn, X, or emerging platforms.",
    },
    {
      number: "05",
      title: "Quality Assurance and Authenticity Verification",
      body: "Every generated variant passes through the Quality Assurance Pipeline -- a multi-stage verification system that checks for brand voice consistency, factual accuracy, emotional calibration alignment, platform compliance, and organic authenticity scoring. Content that falls below the 95% authenticity threshold is flagged for revision.",
    },
    {
      number: "06",
      title: "Content Variant Delivery",
      body: "Verified content variants are packaged for deployment through Multi-Platform Deployment (C03) or delivered as standalone assets. Each variant includes platform targeting metadata, posting recommendations, performance prediction scores, and authenticity certifications.",
    },
  ],
};

export const coreFeatures = {
  eyebrow: "Core Features",
  title: "Content Generation Capabilities",
  accent: "var(--color-denim)",
  featureAccents: [
    "var(--color-crimson)",
    "var(--color-purple)",
    "var(--color-cyan)",
    "var(--color-emerald)",
    "var(--color-amber)",
    "var(--color-denim)",
    "var(--color-violet)",
    "var(--color-rose)",
  ],
  features: [
    {
      number: "01",
      title: "AI-Assisted Content Engine",
      body: "The foundational generation system produces text, visual, video, and audio content from strategic briefs using transformer-based natural language generation and multi-modal production models. The engine does not simply fill templates -- it constructs content architecturally, ensuring each piece serves the broader narrative strategy while meeting platform-specific requirements.",
    },
    {
      number: "02",
      title: "Brand Voice Preservation System",
      body: "A learned voice model captures your organization's linguistic DNA -- vocabulary patterns, sentence structures, tonal registers, stylistic preferences, and rhetorical conventions. Every generated variant is validated against this model, ensuring that the 1,000th piece sounds as authentically yours as the first. Voice drift is detected and corrected before content reaches deployment.",
    },
    {
      number: "03",
      title: "Multi-Format Production",
      body: "Content is generated across four distinct formats from unified briefs. Text production covers long-form, short-form, social, email, and advertising copy. Visual production generates graphics, infographics, and branded imagery. Video production creates scripts, storyboards, and motion guidance. Audio production produces voiceover scripts, podcast segments, and audio ad copy. Each format receives equal strategic attention.",
    },
    {
      number: "04",
      title: "Platform-Specific Optimization",
      body: "Every platform in the 50+ platform ecosystem receives content calibrated for its unique algorithmic preferences, format constraints, and audience behavior patterns. Optimization parameters include content length, visual composition, tone modulation, hashtag strategy, posting frequency, and engagement hook placement -- all tuned per platform.",
    },
    {
      number: "05",
      title: "Content Variant Generator",
      body: "The variant engine produces 1,000-100,000+ content variations per campaign. Variants differ across tone, format, length, visual style, emotional calibration, and audience targeting -- while maintaining strategic consistency through shared narrative briefs. This variant depth enables comprehensive A/B testing, audience segmentation, and platform-specific deployment.",
    },
    {
      number: "06",
      title: "Quality Assurance Pipeline",
      body: "A multi-stage verification system ensures every content variant meets quality thresholds before deployment. QA checks include brand voice consistency scoring, factual accuracy verification, emotional calibration alignment, platform compliance validation, and organic authenticity certification. Content below the 95% threshold is automatically flagged for revision.",
    },
    {
      number: "07",
      title: "Performance Prediction Engine",
      body: "Before deployment, each content variant receives a predicted performance score based on historical data, platform algorithms, audience behavior patterns, and emotional calibration alignment. This prediction capability enables informed content selection -- prioritizing variants with the highest probability of narrative impact.",
    },
    {
      number: "08",
      title: "Adaptive Content Tuning",
      body: "Post-deployment, content performance data flows back into the generation engine. The Adaptive Content Tuning system learns which variants perform best across which platforms, audiences, and contexts -- continuously improving generation quality. This feedback loop ensures that content generation becomes more effective with every campaign.",
    },
  ],
};

export const advancedFeatures = {
  eyebrow: "Advanced Features",
  title: "Premium Content Generation Capabilities",
  sectionAccent: "var(--color-violet)",
  features: [
    {
      title: "Narrative-Coherent Mass Production",
      body: "At scale, content generation risks fragmentation -- thousands of pieces that individually perform but collectively lack coherence. Narrative-Coherent Mass Production ensures that every variant, across all 1,000-100,000+ outputs, maintains narrative coherence with the strategic framework. Coherence scoring validates that individual content pieces reinforce rather than contradict the broader narrative architecture.",
      icon: Network,
      accent: "var(--color-violet)",
    },
    {
      title: "Cross-Engine Emotional Calibration",
      body: "Content variants are not just tonally consistent -- they are emotionally calibrated. Integration with the Cognitive Resonance Engine (CRE) enables content that targets specific emotional dimensions across audience segments. Each variant can be tuned to trigger particular emotional responses, leveraging 144-dimensional emotional mapping for neurological precision in content design.",
      icon: HeartPulse,
      accent: "var(--color-rose)",
    },
    {
      title: "Competitor Content Intelligence",
      body: "The generation engine incorporates competitive content analysis -- identifying content gaps, messaging opportunities, and platform-specific weaknesses in competitor content strategies. Generated variants are positioned to exploit these gaps, filling narrative spaces that competitors have left open or underutilized.",
      icon: BarChart3,
      accent: "var(--color-amber)",
    },
    {
      title: "Real-Time Content Refresh",
      body: "When audience sentiment shifts, competitor narratives evolve, or cultural contexts change, the generation engine can refresh deployed content in real-time. Real-Time Content Refresh modifies existing variants to reflect current conditions without requiring full regeneration -- maintaining content relevance as the narrative landscape evolves.",
      icon: RefreshCw,
      accent: "var(--color-cyan)",
    },
    {
      title: "Multilingual Generation with Cultural Calibration",
      body: "Content is generated across 15+ languages with cultural calibration that goes beyond translation. Each language variant is culturally adapted -- adjusting references, humor, tone, and emotional framing to resonate with linguistic and cultural audiences. Cultural Localization (C16) provides the calibration parameters that ensure multilingual content performs authentically in every target market.",
      icon: Languages,
      accent: "var(--color-emerald)",
    },
  ],
};

export const useCases = {
  eyebrow: "Use Cases",
  title: "Campaign Applications",
  accent: "var(--color-cyan)",
  cases: [
    {
      title: "Product Launch Campaigns",
      body: "Launch campaigns require coordinated content across every digital surface -- announcement articles, social teasers, visual reveals, video previews, and post-launch content sequences. Content Generation produces the full launch content ecosystem from a single strategic brief, ensuring that every piece reinforces the launch narrative with consistent brand voice and platform-specific optimization.",
      metric: "Full ecosystem from one brief",
      icon: Sparkles,
      accent: "var(--color-crimson)",
    },
    {
      title: "Reputation Management Initiatives",
      body: "Reputation campaigns demand rapid content production across multiple formats and platforms. When reputation threats emerge, Content Generation produces response content -- clarification articles, social statements, visual responses, and video messaging -- at the speed the situation demands. 500+ pre-built crisis response playbooks accelerate production when time is critical.",
      metric: "Production at crisis speed",
      icon: ShieldCheck,
      accent: "var(--color-amber)",
    },
    {
      title: "Thought Leadership Programs",
      body: "Sustained thought leadership requires consistent content production over months and years. Content Generation produces article series, social content, presentation materials, and audio content that positions organizational leaders as authoritative voices in their domain -- each piece maintaining the intellectual depth and tonal consistency that thought leadership demands.",
      metric: "Sustained authority positioning",
      icon: Trophy,
      accent: "var(--color-purple)",
    },
    {
      title: "Narrative Seeding Campaigns",
      body: "Trend seeding requires high-volume content designed to establish narrative presence across multiple platforms simultaneously. Content Generation produces the 1,000-10,000+ variants needed to create the appearance of organic narrative emergence -- each piece platform-optimized and authenticity-certified to pass as organic content.",
      metric: "1,000-10,000+ variants per seeding",
      icon: Send,
      accent: "var(--color-cyan)",
    },
    {
      title: "Multi-Regional Campaigns",
      body: "Global campaigns require content localized for 85+ regions across 15+ languages. Content Generation produces region-specific variants that are culturally calibrated -- not merely translated, but adapted in reference, tone, emotional framing, and cultural context to resonate authentically in each target market.",
      metric: "85+ regions, 15+ languages",
      icon: Languages,
      accent: "var(--color-emerald)",
    },
  ],
};

export const benefits = {
  eyebrow: "Benefits",
  title: "Value Delivered Through Content Generation at Scale",
  accent: "var(--color-emerald)",
  gridCols: 2 as const,
  benefits: [
    {
      title: "Production Speed That Matches Narrative Windows",
      body: "Content Generation produces campaign-ready content in hours rather than weeks. When a narrative window opens -- breaking news, competitor action, cultural moment -- the engine delivers the content needed to capitalize on it before the window closes.",
      icon: Zap,
      accent: "var(--color-crimson)",
    },
    {
      title: "Scale Without Quality Degradation",
      body: "Generating 1,000-100,000+ content variants does not mean producing 1,000-100,000 mediocre pieces. The Quality Assurance Pipeline ensures that every variant meets brand voice, emotional calibration, and authenticity thresholds -- maintaining quality regardless of volume.",
      icon: Award,
      accent: "var(--color-amber)",
    },
    {
      title: "Brand Voice Consistency Across Every Touchpoint",
      body: "The Brand Voice Preservation System ensures that audiences encounter a consistent organizational voice across every platform, format, and content type. This consistency builds trust -- audiences learn to recognize and rely on your brand's communication identity.",
      icon: Mic,
      accent: "var(--color-purple)",
    },
    {
      title: "Platform-Native Performance",
      body: "Content optimized for each platform's specific algorithms and audience expectations outperforms generic content distributed uniformly. Platform-specific optimization ensures that content is discovered, engaged with, and shared -- rather than buried by algorithmic filtering.",
      icon: Sliders,
      accent: "var(--color-cyan)",
    },
    {
      title: "Four-Format Coverage from Unified Briefs",
      body: "A single strategic brief produces text, visual, video, and audio content simultaneously. This eliminates format silos -- ensuring that your narrative reaches audiences regardless of their preferred content medium.",
      icon: FileText,
      accent: "var(--color-emerald)",
    },
    {
      title: "Organic Authenticity at 95-98%",
      body: "Content that looks and sounds automated is dismissed by audiences. Perception X2's 95-98% organic authenticity rating ensures that generated content passes as human-created -- maintaining audience trust while operating at machine scale.",
      icon: CheckCircle2,
      accent: "var(--color-rose)",
    },
    {
      title: "Continuous Improvement Through Adaptive Tuning",
      body: "Every campaign improves the next. Adaptive Content Tuning learns from performance data, refining generation parameters to produce more effective content with each iteration. The engine becomes more attuned to your audience, your voice, and your platforms over time.",
      icon: RefreshCw,
      accent: "var(--color-denim)",
    },
    {
      title: "Cost Efficiency at Scale",
      body: "Producing 10,000 content variants through manual teams would require dozens of writers, designers, and producers over months. Content Generation delivers the same output from a single strategic brief -- reducing production costs while increasing output quality and consistency.",
      icon: BarChart3,
      accent: "var(--color-violet)",
    },
  ],
};

export const usps = {
  eyebrow: "Our USPs",
  title: "What Makes Content Generation Unmatched",
  intro: "No other content generation system produces this volume of platform-specific, brand-voice-consistent, authenticity-certified content variants from unified strategic briefs. Most content tools produce individual pieces. Perception X2 produces content ecosystems.",
  accent: "var(--color-violet)",
  usps: [
    {
      title: "1,000-100,000+ Variants Per Campaign",
      body: "No other content generation system produces this volume of platform-specific, brand-voice-consistent, authenticity-certified content variants from unified strategic briefs. Most content tools produce individual pieces. Perception X2 produces content ecosystems.",
      icon: Sparkles,
      accent: "var(--color-crimson)",
    },
    {
      title: "95-98% Organic Authenticity Certification",
      body: "Every content variant is certified for organic authenticity before deployment. This certification is not a marketing claim -- it is a measured metric validated through the Quality Assurance Pipeline. Content below the 95% threshold is rejected and regenerated.",
      icon: Award,
      accent: "var(--color-emerald)",
    },
    {
      title: "Brand Voice as a Learnable Parameter",
      body: "Brand voice is not a style guide document in Perception X2 -- it is a learned model that constrains every generated variant. The Brand Voice Preservation System captures your linguistic DNA and enforces it across 1,000-100,000+ pieces of content, preventing the voice drift that destroys brand trust at scale.",
      icon: Mic,
      accent: "var(--color-purple)",
    },
    {
      title: "Cross-Engine Integration",
      body: "Content Generation does not operate in isolation. It receives emotional calibration from the Cognitive Resonance Engine, narrative architecture from Narrative Engineering, audience intelligence from Audience Targeting, and distribution parameters from Multi-Platform Deployment. This integration produces content that is not just well-written, but strategically engineered.",
      icon: Network,
      accent: "var(--color-cyan)",
    },
    {
      title: "15+ Years of Content Intelligence",
      body: "Perception X2's content generation models are refined through 15+ years of operational data across 300+ elite engagements. This longitudinal intelligence provides generation depth that cannot be replicated through model training alone -- every campaign adds to the engine's understanding of what content performs, where, and why.",
      icon: Trophy,
      accent: "var(--color-amber)",
    },
    {
      title: "99.9999% Uptime SLA",
      body: "Content production cannot afford downtime. Perception X2 operates with 99.9999% uptime -- ensuring that content generation is available whenever narrative windows open, crises emerge, or campaign timelines demand production.",
      icon: Activity,
      accent: "var(--color-denim)",
    },
  ],
  cta: { label: "Explore Other Capabilities", href: "/platform/capabilities" },
};

export const whoItsFor = {
  eyebrow: "Who It's For",
  title: "Organizations That Need Content at Strategic Scale",
  accent: "var(--color-denim)",
  segments: [
    {
      title: "Marketing and Brand Teams",
      body: "Teams managing brand narratives across multiple platforms need content production that scales with campaign ambition. Content Generation enables marketing teams to deploy coordinated content ecosystems -- hundreds or thousands of variants -- while maintaining the brand consistency that stakeholder trust requires.",
      icon: Building2,
      accent: "var(--color-crimson)",
    },
    {
      title: "Reputation Management Organizations",
      body: "Organizations defending or building reputation need rapid content production across every digital surface. Content Generation produces the response content, narrative content, and proactive positioning material that reputation campaigns demand -- at the speed that crisis timelines require.",
      icon: ShieldCheck,
      accent: "var(--color-amber)",
    },
    {
      title: "Political Campaigns and Movements",
      body: "Campaigns managing voter perception need high-volume, platform-specific content across social networks, news platforms, and digital surfaces. Content Generation produces the 1,000-10,000+ variants needed to establish and maintain narrative presence throughout campaign cycles.",
      icon: Vote,
      accent: "var(--color-violet)",
    },
    {
      title: "Government and Public Sector",
      body: "Government organizations communicating policy, managing public trust, and responding to public discourse need content that is authoritative, consistent, and culturally calibrated across diverse populations. Content Generation produces government-grade content at the scale public communication demands.",
      icon: Landmark,
      accent: "var(--color-emerald)",
    },
    {
      title: "Global Enterprises and Multinationals",
      body: "Organizations operating across 85+ regions and 15+ languages need content that is locally authentic while globally consistent. Content Generation produces multilingual, culturally calibrated content that maintains brand coherence across every market.",
      icon: Globe,
      accent: "var(--color-cyan)",
    },
    {
      title: "Public Figures and Personal Brands",
      body: "Individuals managing public perception need content that maintains personal voice consistency while scaling across platforms and contexts. Content Generation produces content calibrated to the individual's authentic voice while ensuring platform-native performance.",
      icon: User,
      accent: "var(--color-purple)",
    },
  ],
};

export const whyChooseUs = {
  eyebrow: "Why Choose Perception X2",
  title: "Proven Content Generation at Unmatched Scale",
  accent: "var(--color-amber)",
  reasons: [
    {
      title: "15+ Years of Operational Excellence",
      body: "Perception X2 has been generating content at scale for 15+ years -- refining its models, expanding its capabilities, and delivering results across 300+ elite engagements. This is not a new tool. It is a battle-tested production engine with a decade and a half of operational intelligence.",
      icon: Trophy,
      accent: "var(--color-crimson)",
    },
    {
      title: "300+ Elite Client Engagements",
      body: "Content Generation has been deployed across 300+ elite engagements spanning government, enterprise, political, and institutional contexts. Each engagement has contributed to the engine's intelligence -- making it more effective with every campaign.",
      icon: Globe,
      accent: "var(--color-amber)",
    },
    {
      title: "9 Proprietary AI Platforms",
      body: "Content Generation is one of nine interconnected AI platforms. This ecosystem integration means content is not generated in isolation -- it is informed by emotional intelligence, audience targeting, narrative engineering, and distribution optimization from across the Perception X2 platform suite.",
      icon: Cpu,
      accent: "var(--color-cyan)",
    },
    {
      title: "99.9999% Uptime SLA",
      body: "Content production cannot afford downtime. Perception X2 operates with 99.9999% uptime -- ensuring that content generation is available whenever narrative windows open, crises emerge, or campaign timelines demand production.",
      icon: Activity,
      accent: "var(--color-emerald)",
    },
    {
      title: "Zero Security Incidents",
      body: "Content strategies, brand voice models, and campaign briefs are among an organization's most sensitive intellectual property. Perception X2 has maintained zero security incidents across 15+ years of operation -- protecting the strategic intelligence that powers content generation.",
      icon: ShieldCheck,
      accent: "var(--color-purple)",
    },
    {
      title: "Proprietary AI Infrastructure",
      body: "Every server, every data center, every AI platform -- proprietary. Zero third-party cloud dependencies ensure your content strategy never traverses unsecured external systems. This sovereign capability produces performance and security that cloud-dependent competitors cannot match.",
      icon: Server,
      accent: "var(--color-denim)",
    },
  ],
};

export const relatedCapabilities = {
  eyebrow: "Related Capabilities",
  title: "The Perception X2 Content Pipeline",
  accent: "var(--color-cyan)",
  capabilities: [
    {
      name: "Narrative Engineering",
      href: "/capabilities/narrative-engineering",
      body: "Narrative Engineering designs the strategic frameworks that Content Generation executes. Story frameworks, conflict arcs, resolution pathways, and cognitive bias architectures provide the blueprint from which content variants are produced.",
      icon: BookOpen,
      accent: "var(--color-crimson)",
    },
    {
      name: "Content Optimization",
      href: "/capabilities/content-optimization",
      body: "Content Optimization refines generated content for maximum performance. A/B testing, emotional calibration adjustments, and algorithmic tuning ensure that each variant achieves its highest possible narrative impact.",
      icon: Sliders,
      accent: "var(--color-cyan)",
    },
    {
      name: "Message Architecture",
      href: "/message-architecture",
      body: "Message Architecture provides the messaging frameworks that structure content generation. Key messages, supporting narratives, and message hierarchies ensure that every content variant communicates consistently.",
      icon: FileText,
      accent: "var(--color-purple)",
    },
    {
      name: "Multi-Platform Deployment",
      href: "/capabilities/multi-platform-deployment",
      body: "Multi-Platform Deployment distributes generated content across 50+ digital platforms with native optimization. Content Generation produces the material; Multi-Platform Deployment ensures it reaches every target surface with platform-specific formatting and timing.",
      icon: Send,
      accent: "var(--color-emerald)",
    },
  ],
  engines: [
    {
      name: "Narrative Generation Engine (NGE)",
      href: "/engines/narrative-generation",
      body: "The transformer-based NLG engine that powers multi-variant content generation with brand voice preservation.",
      icon: Cpu,
      accent: "var(--color-cyan)",
    },
    {
      name: "Cognitive Resonance Engine (CRE)",
      href: "/engines/cognitive-resonance",
      body: "The emotional intelligence layer providing 144-dimensional mapping and 200+ cognitive bias integration.",
      icon: HeartPulse,
      accent: "var(--color-rose)",
    },
  ],
  services: [
    {
      name: "Content and Narrative Engineering",
      href: "/services/content-narrative-engineering",
      body: "Our Content and Narrative Engineering service combines strategic narrative design with production-scale content generation. Content Generation is the production engine; this service provides the strategic direction, brand voice modeling, and campaign management that directs it.",
      icon: Network,
      accent: "var(--color-emerald)",
    },
  ],
};

export const faq = {
  eyebrow: "Frequently Asked Questions",
  title: "Frequently Asked Questions About Content Generation",
  accent: "var(--color-purple)",
  items: [
    { q: "What is Perception X2 Content Generation?", a: "Perception X2 Content Generation is an AI-powered content production capability that generates 1,000-100,000+ content variants per campaign across text, visual, video, and audio formats. Each variant is optimized for its target platform, calibrated for emotional resonance, and certified for 95-98% organic authenticity." },
    { q: "How does Perception X2 maintain brand voice across thousands of content variants?", a: "The Brand Voice Preservation System learns your organization's linguistic patterns, tonal preferences, vocabulary conventions, and stylistic signatures. Every generated variant is validated against this learned voice model -- ensuring that the 1,000th piece sounds as authentically yours as the first. Voice drift is detected and corrected before content reaches deployment." },
    { q: "What formats does Perception X2 generate content in?", a: "Perception X2 produces content across four formats: text (articles, social posts, press releases, ad copy, email sequences), visual (infographics, social graphics, data visualizations, branded imagery), video (short-form clips, explainer sequences, motion graphics, campaign videos), and audio (podcast segments, voiceover scripts, audio advertisements)." },
    { q: "How many content variants can Perception X2 produce per campaign?", a: "Perception X2 generates between 1,000 and 100,000+ content variants per campaign. Each variant is platform-specific, brand-voice-consistent, and authenticity-certified. The actual volume depends on campaign scope, platform coverage, audience segmentation depth, and format requirements." },
    { q: "What does 95-98% organic authenticity mean?", a: "Organic authenticity rating measures how closely AI-generated content resembles human-created content. At 95-98%, Perception X2's output is statistically indistinguishable from content produced by human writers, designers, and producers. Content below the 95% threshold is flagged for revision by the Quality Assurance Pipeline." },
    { q: "How does platform-specific optimization work?", a: "Each of the 50+ platforms in Perception X2's coverage has distinct algorithmic preferences, format constraints, and audience expectations. The Platform-Specific Optimization engine adjusts content length, visual composition, tone, hashtag strategy, posting cadence, and engagement hooks per platform -- producing content that feels native to each environment." },
    { q: "Can Perception X2 generate content in multiple languages?", a: "Yes. Perception X2 generates content across 15+ languages with cultural calibration that goes beyond translation. Each language variant is culturally adapted -- adjusting references, humor, tone, and emotional framing to resonate authentically with linguistic and cultural audiences across 85+ regions." },
    { q: "How does Content Generation integrate with other Perception X2 capabilities?", a: "Content Generation receives strategic inputs from Narrative Engineering (C01) and Message Architecture (C19), emotional calibration from the Cognitive Resonance Engine, audience intelligence from Audience Targeting (C08), and feeds output to Content Optimization (C09) and Multi-Platform Deployment (C03). This integration ensures content is strategically informed and deployment-ready." },
    { q: "What is the Quality Assurance Pipeline?", a: "The Quality Assurance Pipeline is a multi-stage verification system that validates every content variant before deployment. QA checks include brand voice consistency scoring, factual accuracy verification, emotional calibration alignment, platform compliance validation, and organic authenticity certification. Content below the 95% authenticity threshold is automatically flagged for revision." },
    { q: "How fast can Perception X2 produce content?", a: "Content Generation operates at the speed of narrative demand. Campaigns requiring 1,000-10,000 variants can be produced within hours. Larger campaigns producing 50,000-100,000+ variants are produced within defined production windows specified during campaign planning. The engine scales production speed with campaign ambition." },
    { q: "What industries benefit most from Content Generation?", a: "Content Generation delivers measurable value across marketing and brand teams, reputation management organizations, political campaigns, government and public sector entities, and global enterprises. Any organization requiring coordinated, high-volume, platform-specific content production benefits from the capability." },
    { q: "Is the generated content detectable as AI-created?", a: "Perception X2's content is designed to achieve 95-98% organic authenticity -- meaning it is statistically indistinguishable from human-created content. The Brand Voice Preservation System, Quality Assurance Pipeline, and emotional calibration systems work together to ensure that generated content passes as authentic, human-produced material." },
  ],
};

export const paa = {
  eyebrow: "People Also Ask",
  title: "What People Ask About AI Content Generation",
  accent: "var(--color-cyan)",
  items: [
    { q: "What is AI content generation and how does it work?", a: "AI content generation uses artificial intelligence models to produce text, visual, video, and audio content from strategic briefs. Perception X2's approach goes beyond simple generation -- it integrates brand voice modeling, emotional calibration, platform optimization, and quality assurance to produce content that is strategically engineered rather than merely generated." },
    { q: "How does AI content creation maintain brand authenticity?", a: "Brand authenticity is maintained through the Brand Voice Preservation System -- a learned model that captures an organization's linguistic patterns, tonal preferences, and stylistic conventions. Every generated variant is validated against this model, ensuring consistency across thousands of pieces. The Quality Assurance Pipeline adds an additional authenticity verification layer, certifying 95-98% organic authenticity before deployment." },
    { q: "What is multi-format content generation?", a: "Multi-format content generation produces content across text, visual, video, and audio formats from unified strategic briefs. Rather than requiring separate teams and workflows for each format, a single narrative framework generates all format types simultaneously -- ensuring consistency while covering every content medium your audience consumes." },
    { q: "How does platform-specific content optimization improve performance?", a: "Platform-specific optimization tailors content to each platform's algorithmic preferences, format constraints, and audience behavior patterns. Content optimized for Instagram differs from content optimized for LinkedIn, which differs from content optimized for X. This native optimization ensures content is discovered, engaged with, and shared -- rather than filtered out by platform algorithms." },
    { q: "Why is content velocity important for perception management?", a: "Content velocity -- the speed at which content can be produced and deployed -- determines whether organizations can capitalize on narrative windows. Breaking news, competitor actions, and cultural moments create opportunities measured in hours. Organizations that cannot produce content at matching speed lose narrative influence to faster competitors." },
    { q: "What role does AI play in content quality assurance?", a: "AI powers the Quality Assurance Pipeline's multi-stage verification -- checking brand voice consistency, factual accuracy, emotional calibration, platform compliance, and organic authenticity for every content variant. AI-based QA can validate 1,000-100,000+ variants at the depth and speed that manual review cannot achieve." },
  ],
};

export const cta = {
  eyebrow: "Get Started",
  title: "Ready to Generate Content at Strategic Scale?",
  intro: "The gap between narrative strategy and content execution is where perception management campaigns fail. Perception X2 Content Generation closes that gap -- producing 1,000-100,000+ platform-specific, brand-voice-consistent, 95-98% authentic content variants from unified strategic briefs.",
  promises: [
    "Schedule a confidential consultation to explore how Content Generation can transform your content operations",
    "All consultations are conducted under strict confidentiality with zero security incidents in 15+ years of operations",
  ],
  ctaPrimary: { label: "Request a Consultation", href: "/#contact" },
  ctaSecondary: { label: "Explore Other Capabilities", href: "/platform/capabilities" },
  trustBar: ["1,000-100,000+ Variants", "50+ Platforms", "95-98% Authenticity", "15+ Years", "Zero Incidents"],
  accent: "var(--color-crimson)",
  secondaryAccent: "var(--color-cyan)",
};
